import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Clock, MapPin, IndianRupee, Plus, Trash2, Save, 
  Building2, Calculator, Calendar 
} from "lucide-react";

const GeneralSettingsTab = ({
  settings,
  setSettings,
  canManage,
  saving,
  handleSaveSettings,
  locations,
  setShowAddLocation,
  handleDeleteLocation,
}) => {
  const weekdays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const weeklyOffs = settings.weekly_offs || ["sunday"];

  const handleWeeklyOffToggle = (day) => {
    if (!canManage) return;
    const isOff = weeklyOffs.includes(day);
    const newOffs = isOff
      ? weeklyOffs.filter(d => d !== day)
      : [...weeklyOffs, day];
    setSettings({ ...settings, weekly_offs: newOffs });
  };

  return (
    <>
      <OfficeTimingCard 
        settings={settings} 
        setSettings={setSettings} 
        canManage={canManage} 
      />

      <DeductionRulesCard 
        settings={settings} 
        setSettings={setSettings} 
        canManage={canManage} 
      />

      <SalaryCalculationCard 
        settings={settings} 
        setSettings={setSettings} 
        canManage={canManage}
        weekdays={weekdays}
        weeklyOffs={weeklyOffs}
        handleWeeklyOffToggle={handleWeeklyOffToggle}
      />

      <VerificationCard 
        settings={settings} 
        setSettings={setSettings} 
        canManage={canManage} 
      />

      <LocationsCard 
        locations={locations}
        canManage={canManage}
        setShowAddLocation={setShowAddLocation}
        handleDeleteLocation={handleDeleteLocation}
      />

      {canManage && (
        <div className="flex justify-end">
          <Button 
            onClick={handleSaveSettings}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? "Saving..." : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      )}
    </>
  );
};

const OfficeTimingCard = ({ settings, setSettings, canManage }) => (
  <Card className="border-slate-200 dark:border-slate-700">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-lg">
        <Clock className="w-5 h-5 text-emerald-600" />
        Office Timing
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <Label htmlFor="timezone" className="text-sm font-medium text-blue-700 dark:text-blue-300">
          Business Timezone (for accurate punch time)
        </Label>
        <Select
          value={settings.timezone || "Asia/Kolkata"}
          onValueChange={(value) => setSettings({ ...settings, timezone: value })}
          disabled={!canManage}
        >
          <SelectTrigger id="timezone" className="mt-2" data-testid="timezone-select">
            <SelectValue placeholder="Select timezone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Asia/Kolkata">India (IST - Asia/Kolkata)</SelectItem>
            <SelectItem value="Asia/Dubai">Dubai (GST - Asia/Dubai)</SelectItem>
            <SelectItem value="Asia/Singapore">Singapore (SGT - Asia/Singapore)</SelectItem>
            <SelectItem value="Asia/Tokyo">Japan (JST - Asia/Tokyo)</SelectItem>
            <SelectItem value="Europe/London">UK (GMT/BST - Europe/London)</SelectItem>
            <SelectItem value="America/New_York">US Eastern (EST/EDT - America/New_York)</SelectItem>
            <SelectItem value="America/Los_Angeles">US Pacific (PST/PDT - America/Los_Angeles)</SelectItem>
            <SelectItem value="Australia/Sydney">Australia (AEST - Australia/Sydney)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
          Punch times will be recorded and displayed in this timezone
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="start_time">Office Start Time</Label>
          <Input
            id="start_time"
            type="time"
            value={settings.office_start_time}
            onChange={(e) => setSettings({ ...settings, office_start_time: e.target.value })}
            disabled={!canManage}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="end_time">Office End Time</Label>
          <Input
            id="end_time"
            type="time"
            value={settings.office_end_time}
            onChange={(e) => setSettings({ ...settings, office_end_time: e.target.value })}
            disabled={!canManage}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="working_hours">Working Hours/Day</Label>
          <Input
            id="working_hours"
            type="number"
            step="0.5"
            value={settings.working_hours_per_day}
            onChange={(e) => setSettings({ ...settings, working_hours_per_day: parseFloat(e.target.value) })}
            disabled={!canManage}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="half_day">Half Day Hours</Label>
          <Input
            id="half_day"
            type="number"
            step="0.5"
            value={settings.half_day_hours}
            onChange={(e) => setSettings({ ...settings, half_day_hours: parseFloat(e.target.value) })}
            disabled={!canManage}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="grace">Late Grace Period (mins)</Label>
          <Input
            id="grace"
            type="number"
            value={settings.late_grace_minutes}
            onChange={(e) => setSettings({ ...settings, late_grace_minutes: parseInt(e.target.value) })}
            disabled={!canManage}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="early_punch">Early Punch-In Allowed (mins)</Label>
          <Input
            id="early_punch"
            type="number"
            value={settings.allow_early_punch_in_minutes}
            onChange={(e) => setSettings({ ...settings, allow_early_punch_in_minutes: parseInt(e.target.value) })}
            disabled={!canManage}
          />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DeductionRulesCard = ({ settings, setSettings, canManage }) => (
  <Card className="border-slate-200 dark:border-slate-700">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-lg">
        <IndianRupee className="w-5 h-5 text-emerald-600" />
        Deduction & Overtime Rules
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="late_penalty">Late Penalty Amount</Label>
          <Input
            id="late_penalty"
            type="number"
            value={settings.late_penalty_amount}
            onChange={(e) => setSettings({ ...settings, late_penalty_amount: parseFloat(e.target.value) })}
            disabled={!canManage}
          />
          <p className="text-xs text-slate-500">Deducted per late entry (when using fixed deduction)</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="overtime_rate">Overtime Rate Multiplier</Label>
          <Input
            id="overtime_rate"
            type="number"
            step="0.1"
            value={settings.overtime_rate_multiplier}
            onChange={(e) => setSettings({ ...settings, overtime_rate_multiplier: parseFloat(e.target.value) })}
            disabled={!canManage}
          />
          <p className="text-xs text-slate-500">e.g., 1.5x means 150% of hourly rate</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const SalaryCalculationCard = ({ settings, setSettings, canManage, weekdays, weeklyOffs, handleWeeklyOffToggle }) => (
  <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-lg">
        <Calculator className="w-5 h-5 text-emerald-600" />
        Salary Calculation Settings
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-6">
      <div>
        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Working Hours Thresholds</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="full_day_min_hours">Full Day Min Hours</Label>
            <Input
              id="full_day_min_hours"
              type="number"
              step="0.5"
              value={settings.full_day_min_hours || 9}
              onChange={(e) => setSettings({ ...settings, full_day_min_hours: parseFloat(e.target.value) })}
              disabled={!canManage}
            />
            <p className="text-xs text-slate-500">Hours &gt;= this = Full Day</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="half_day_min_hours">Half Day Min Hours</Label>
            <Input
              id="half_day_min_hours"
              type="number"
              step="0.5"
              value={settings.half_day_min_hours || 4.5}
              onChange={(e) => setSettings({ ...settings, half_day_min_hours: parseFloat(e.target.value) })}
              disabled={!canManage}
            />
            <p className="text-xs text-slate-500">Hours &gt;= this (but less than Full) = Half Day</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="salary_calculation_days">Salary Calculation Days</Label>
            <Input
              id="salary_calculation_days"
              type="number"
              value={settings.salary_calculation_days || 30}
              onChange={(e) => setSettings({ ...settings, salary_calculation_days: parseInt(e.target.value) })}
              disabled={!canManage}
            />
            <p className="text-xs text-slate-500">Per Day Salary = Monthly / This</p>
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Weekly Off Days</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {weekdays.map((day) => {
            const isOff = weeklyOffs.includes(day);
            return (
              <WeekdayToggle 
                key={day} 
                day={day} 
                isOff={isOff} 
                canManage={canManage}
                onToggle={handleWeeklyOffToggle}
              />
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-2">Selected days will be excluded from working days calculation</p>
      </div>

      <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-emerald-600" />
          <div>
            <p className="font-medium">Exclude Company Holidays</p>
            <p className="text-sm text-slate-500">Auto-exclude holidays from working days calculation</p>
          </div>
        </div>
        <Switch
          checked={settings.exclude_holidays !== false}
          onCheckedChange={(checked) => setSettings({ ...settings, exclude_holidays: checked })}
          disabled={!canManage}
        />
      </div>

      <LateDeductionSection settings={settings} setSettings={setSettings} canManage={canManage} />
    </CardContent>
  </Card>
);

const WeekdayToggle = ({ day, isOff, canManage, onToggle }) => (
  <div
    className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-all ${
      isOff
        ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700"
        : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
    } ${!canManage ? "opacity-60 cursor-not-allowed" : "hover:border-amber-400"}`}
    onClick={() => onToggle(day)}
  >
    <span className={`text-xs font-medium capitalize ${isOff ? "text-amber-700 dark:text-amber-400" : "text-slate-600 dark:text-slate-400"}`}>
      {day.slice(0, 3)}
    </span>
    <Switch checked={isOff} disabled={!canManage} className="scale-75" />
  </div>
);

const LateDeductionSection = ({ settings, setSettings, canManage }) => (
  <div>
    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Late Mark Deduction Rule</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="late_deduction_type">Deduction Type</Label>
        <Select
          value={settings.late_deduction_type || "fixed"}
          onValueChange={(value) => setSettings({ ...settings, late_deduction_type: value })}
          disabled={!canManage}
        >
          <SelectTrigger id="late_deduction_type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fixed">Fixed Amount per Late</SelectItem>
            <SelectItem value="convert_to_half_day">Convert to Half Day</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {settings.late_deduction_type === "convert_to_half_day" && (
        <div className="space-y-2">
          <Label htmlFor="late_marks_to_half_day">Late Marks = 1 Half Day</Label>
          <Input
            id="late_marks_to_half_day"
            type="number"
            min="1"
            value={settings.late_marks_to_half_day || 3}
            onChange={(e) => setSettings({ ...settings, late_marks_to_half_day: parseInt(e.target.value) })}
            disabled={!canManage}
          />
          <p className="text-xs text-slate-500">e.g., 3 means every 3 late marks = 1 half day deduction</p>
        </div>
      )}
    </div>
  </div>
);

const VerificationCard = ({ settings, setSettings, canManage }) => (
  <Card className="border-slate-200 dark:border-slate-700">
    <CardHeader>
      <CardTitle className="text-lg">Verification Requirements</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
        <div>
          <p className="font-medium">Require Selfie Verification</p>
          <p className="text-sm text-slate-500">Staff must take a selfie when punching in</p>
        </div>
        <Switch
          checked={settings.require_selfie}
          onCheckedChange={(checked) => setSettings({ ...settings, require_selfie: checked })}
          disabled={!canManage}
        />
      </div>
      <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
        <div>
          <p className="font-medium">Require GPS Location</p>
          <p className="text-sm text-slate-500">Staff must share location when punching in</p>
        </div>
        <Switch
          checked={settings.require_gps}
          onCheckedChange={(checked) => setSettings({ ...settings, require_gps: checked })}
          disabled={!canManage}
        />
      </div>
    </CardContent>
  </Card>
);

const LocationsCard = ({ locations, canManage, setShowAddLocation, handleDeleteLocation }) => (
  <Card className="border-slate-200 dark:border-slate-700">
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="flex items-center gap-2 text-lg">
        <MapPin className="w-5 h-5 text-emerald-600" />
        Office Locations (Geo-fence)
      </CardTitle>
      {canManage && (
        <Button size="sm" onClick={() => setShowAddLocation(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Location
        </Button>
      )}
    </CardHeader>
    <CardContent>
      {locations.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No office locations configured</p>
          <p className="text-sm">Staff can punch in from anywhere when no locations are set</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Coordinates</TableHead>
                <TableHead>Radius</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.map((loc) => (
                <LocationRow 
                  key={loc.location_id} 
                  location={loc} 
                  canManage={canManage}
                  handleDeleteLocation={handleDeleteLocation}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </CardContent>
  </Card>
);

const LocationRow = ({ location, canManage, handleDeleteLocation }) => (
  <TableRow>
    <TableCell className="font-medium">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-emerald-600" />
        {location.name}
      </div>
    </TableCell>
    <TableCell className="text-slate-500 text-sm">
      {location.address || "-"}
    </TableCell>
    <TableCell className="text-xs font-mono">
      {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
    </TableCell>
    <TableCell>{location.radius_meters}m</TableCell>
    {canManage && (
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="sm"
          className="text-red-600 hover:text-red-700"
          onClick={() => handleDeleteLocation(location.location_id)}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </TableCell>
    )}
  </TableRow>
);

export default GeneralSettingsTab;
